/**
 * novaDamage.ts
 * Shared "Nova takes a contact hit" resolution for one fixed physics step.
 *
 * Extracted from `combatStep.ts` so enemy body contact and spike-hazard
 * contact apply *identical* consequences. Before this module existed the
 * enemy path was the only implementation, which is why the hazard layer had
 * no damage behaviour at all — see `hazardStep.ts`.
 *
 * Architecture boundary: NO React, NO async, NO rendering.
 *
 * @module novaDamage
 */

import { NOVA_WIDTH, NOVA_INVINCIBILITY_FRAMES } from '../../constants/gameConstants';
import { KNOCKBACK_VX, KNOCKBACK_VY } from './physics';
import { dispatch } from './gameEventDispatch';
import { resetWeaponOnHit } from './weaponPickupStep';
import type { NovaRuntime } from './gameState';

/**
 * Apply a single point of contact damage to Nova, with invincibility frames,
 * weapon loss, and knockback away from the damage source.
 *
 * No-op while Nova already has invincibility frames, so a cluster of
 * overlapping hazards or enemies cannot drain multiple hearts in one tick.
 *
 * @param nova          - Nova runtime state, mutated in place.
 * @param sourceCenterX - World-space X centre of whatever dealt the damage;
 *                        decides which way Nova is knocked back.
 * @returns True if the hit landed, false if it was absorbed by i-frames.
 */
export function applyNovaContactDamage(nova: NovaRuntime, sourceCenterX: number): boolean {
  if (nova.invincibilityFrames !== 0) return false;

  nova.health = Math.max(0, nova.health - 1);
  nova.invincibilityFrames = NOVA_INVINCIBILITY_FRAMES;
  nova.state = 'HURT';
  nova.hurtFlash = true;
  dispatch.hurt();
  // DECISION: Drop the active weapon when Nova takes a hit. The Shield
  // power-up guard will be added at this call site once Shield state
  // exists (TODO(shield-powerup) in weaponPickupStep.ts).
  resetWeaponOnHit();
  // DECISION: Dispatch low-health warning when health drops to 1.
  if (nova.health === 1) dispatch.lowHealth();

  // DECISION: Apply knockback velocity away from the source centre.
  // Horizontal direction is determined by the relative position of Nova
  // to the source. Vertical is always upward for a visible recoil hop.
  const novaCenterX = nova.x + NOVA_WIDTH / 2;
  nova.vx = novaCenterX >= sourceCenterX ? KNOCKBACK_VX : -KNOCKBACK_VX;
  nova.vy = KNOCKBACK_VY;
  nova.isGrounded = false;
  return true;
}
